/**
 * ===========================================================================
 *  CHINTU CONFIGURATION — the only file you need to edit
 * ===========================================================================
 *
 * Pure data. No logic, no React, no runtime imports (the type import below is
 * erased at build time). Everything Chintu says, every button, and the rule
 * that decides whether he actually turns up lives here.
 *
 * Three things to know before editing:
 *
 *  1. BUTTON LABELS ARE COSMETIC. `id` is the contract. Rename a label freely
 *     and every rule keeps working, because rules match on ids.
 *
 *  2. ONE MATCHER SHAPE, USED TWICE. `when` decides both which copy variant a
 *     message shows and what the finale does. Rules are evaluated in order,
 *     first match wins. `when: {}` matches everything — always put one last.
 *     Omit a message = don't care. Use an array = any of those answers.
 *
 *  3. "ignored" IS A RESERVED ANSWER. It's recorded when someone swipes the
 *     placard away or taps outside instead of pressing a button, so you can
 *     write rules about silence.
 *
 *  4. COPY TOKENS. Any kicker/headline/sub can use {amount} for the live
 *     committed bill and {threshold} for the milestone that was crossed.
 *     Never type the number by hand — it goes stale the moment a threshold
 *     moves, and nothing will tell you.
 *
 * Adding a third milestone: append to CHINTU_MESSAGES. Nothing else changes.
 */

import type {
  ChintuBehaviour,
  ChintuConfettiConfig,
  ChintuFinale,
  ChintuMessage,
  ChintuStoryCardContent,
} from "./chintuStoryEngine";

/** Reserved answer id recorded when a placard is dismissed without a button. */
export const IGNORED = "ignored";

// ---------------------------------------------------------------------------
//  MESSAGES — the placards, in threshold order
// ---------------------------------------------------------------------------

export const CHINTU_MESSAGES: ChintuMessage[] = [
  {
    id: "first",
    at: 500,
    // Rope length is the escalation — 44px reads as mildly curious.
    motion: { ropeLen: 44, swingMs: 2600, deg: 3.5, shake: false },

    buttons: [
      { id: "dismiss", label: "I don't care", style: "ghost" },
      // `opens: "story"` is presentational only — it shows CHINTU_STORY_CARD
      // instead of closing. The answer "who" is still recorded, so rules can
      // treat curiosity as its own signal.
      { id: "who", label: "Who is Chintu?", style: "accent", opens: "story" },
    ],

    variants: [
      {
        when: {},
        copy: {
          kicker: "₹{amount} crossed",
          headline: "Chintu is a little excited.",
          sub: "He welcomes you to your table",
        },
      },
    ],
  },

  {
    id: "second",
    at: 1200,
    // 96px and ±8° reads as barely contained. shake rattles the page under him.
    motion: { ropeLen: 96, swingMs: 1150, deg: 8, shake: true },

    buttons: [
      { id: "dismiss", label: "Push him away", style: "ghost" },
      {
        id: "encourage",
        label: "Let him come, I'll deal",
        style: "accent",
        reaction: "Your dining would be disturbed if you cross 1937, we warned you.So dont blame us.",
      },
    ],

    // First match wins, so the specific cases come before the catch-all.
    // This is what makes him seem to remember how he was treated.
    variants: [
      {
        when: { first: "who" },
        copy: {
          kicker: "₹{amount} crossed",
          headline: "You are almost reaching 1937",
          sub: "He will get excited and might come to your table and disturb you if you cross 1937",
        },
      },
      {
        when: { first: "dismiss" },
        copy: {
          kicker: "₹{amount} crossed",
          headline: "You didnt care last time, he is little dissapointed",
          sub: "But he is still excited that you might cross 1937, if you do , he might not be able to hold it, and he might come and disturb you.",
        },
      },
      {
        when: {},
        copy: {
          kicker: "₹{amount} crossed",
          headline: "Chintu can't hold it in anymore.",
          sub: "He may come over and disturb you. Consider this a warning, not a threat.",
        },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
//  FINALE — does he actually show up?
// ---------------------------------------------------------------------------

/**
 * Outcomes: "ARRIVE" plays the 3D walk-in and dance. "NO_SHOW" drops the
 * closing placard below. "NOTHING" ends the arc silently.
 *
 * CONSENT IS OPT-OUT, NOT OPT-IN. He comes unless someone explicitly pushed
 * him away. That matters because a table can reach the finale having seen
 * NOTHING: order ₹2,000 in the very first round and crossedBeat() collapses
 * the whole ladder to the finale, so messages 1 and 2 never appear. Under an
 * opt-in rule those diners got the no-show card — accused of rejecting a
 * monkey they'd never been introduced to.
 *
 * Silence, swiping a card away, and dismissing the ₹500 teaser all leave him
 * coming. Only "Push him away" on message 2 — the card that actually asks —
 * stops him.
 *
 * Recipes — replace `rules` with any of these:
 *
 *   unless explicitly pushed away (current)
 *     [{ when: { second: "dismiss" }, then: "NO_SHOW" }, { when: {}, then: "ARRIVE" }]
 *
 *   only if actively encouraged (opt-in — re-read the warning above first)
 *     [{ when: { second: "encourage" }, then: "ARRIVE" }, { when: {}, then: "NO_SHOW" }]
 *
 *   only if he was welcomed BOTH times
 *     [{ when: { first: "who", second: "encourage" }, then: "ARRIVE" }, { when: {}, then: "NO_SHOW" }]
 *
 *   rejected either time is enough to stop him
 *     [{ when: { first: "dismiss" }, then: "NO_SHOW" },
 *      { when: { second: "dismiss" }, then: "NO_SHOW" },
 *      { when: {}, then: "ARRIVE" }]
 *
 *   he always comes
 *     [{ when: {}, then: "ARRIVE" }]
 */
export const CHINTU_FINALE: ChintuFinale = {
  /**
   * 1937 — Chintu's number. The message-2 copy promises he turns up if you
   * cross it, and the confetti banner announces it, so this value, that
   * promise and that payoff all have to agree. Change it and the banner
   * follows automatically ({threshold}), but re-read the copy above.
   */
  at: 1937,

  rules: [
    { when: { second: "dismiss" }, then: "NO_SHOW" },
    { when: {}, then: "ARRIVE" },
  ],

  /**
   * Escape hatch for anything the table above can't express. Return an
   * outcome to force it, or null/undefined to fall through to `rules`.
   * Left unset by default — prefer rules, they're inspectable.
   */
  // override: (answers) => (answers.first === "who" ? "ARRIVE" : null),

  noShow: {
    motion: { ropeLen: 30, swingMs: 3400, deg: 2, shake: false },
    buttons: [{ id: "ack", label: "Fine.", style: "ghost" }],
    copy: {
      kicker: "₹{amount} crossed",
      headline: "Chintu has decided not to come.",
      sub: "When you said push him away, security has literally pushed him, so he fell down and got injured, but he hopes you're happy. He isn't.",
    },
  },
};

// ---------------------------------------------------------------------------
//  THE STORY CARD — shown by any button with opens: "story"
// ---------------------------------------------------------------------------

/**
 * Deliberately not a sales pitch. Chintu isn't excited that you're SPENDING —
 * he's excited that a growing bill means nobody got up to leave. That's what
 * keeps him charming instead of mercenary. The "can't count past nine" and
 * "only knows one dance" beats also quietly explain the brand name and the
 * fact that we ship exactly one dance clip.
 */
export const CHINTU_STORY_CARD: ChintuStoryCardContent = {
  name: "CHINTU",
  role: "CNO (self-appointed 😅)",
  meta: "NineFinds · ID 000-009 ·",


  paragraphs: [
  "Chintu dreamed of becoming an accountant at NineFinds. But he doesnt know numbers.",
  "Nobody hired him. So he hired himself as the Chief Numbers Officer. For the last few years, he has stood beside tables, learning numbers.",
  "Somewhere along the way, he became convinced that every number has a personality. And according to him, 1937 is just like him.",
  "Also he says he knows one dance. He learned it years ago at his cousin's wedding... a wedding he wasn't invited to."
],

  footnotes: [
    "Compensation: bananas,Employee of the Month (self-awarded).",
    "Reports to: nobody.",
  ],

  okLabel: "OK",
};

// ---------------------------------------------------------------------------
//  THE BURST — coins and streamers when he starts dancing
// ---------------------------------------------------------------------------

/**
 * Fires on the exact beat the dance begins, not when he walks in — so the
 * turn, the camera swing and the burst land together as one hit.
 *
 * Deliberately DOM/CSS rather than three.js particles: 28 transparent sprites
 * in the scene would add draw calls and transparency sorting on top of a
 * skinned character, which is what kills framerate on low-end Android. CSS
 * transforms composite off the main thread and cost the WebGL scene nothing.
 */
export const CHINTU_CONFETTI: ChintuConfettiConfig = {
  enabled: true,

  /** Total pieces. Above ~40 the burst stops reading as individual objects. */
  pieceCount: 28,
  /** Share of pieces that are coins; the rest are streamers. */
  coinRatio: 0.4,

  coinColor: "#f5c451",
  coinEdgeColor: "#c9922a",
  ribbonColors: ["#3ddc84", "#1faa62", "#8ef0b8"],

  /** How long a piece takes to fly out and fall. Randomised +/-25% per piece. */
  burstMs: 2600,

  /**
   * `{threshold}` is Chintu's number — the milestone that summoned him, which
   * is the whole point of the celebration. `{amount}` is what the table
   * actually spent (e.g. 2,140 if one big round blew past it). Both are
   * grouped Indian-style.
   *
   * Quoting the threshold keeps the payoff consistent with his story: he
   * turns up for 1937, not for whatever number happens to be on the bill.
   */
  bannerText: "₹{threshold} crossed",
  /** How long the banner holds at full opacity before fading out. */
  bannerHoldMs: 3000,
};

// ---------------------------------------------------------------------------
//  BEHAVIOUR — timing, assets, plumbing
// ---------------------------------------------------------------------------

export const CHINTU_BEHAVIOUR: ChintuBehaviour = {
  /**
   * Minimum gap between two placards. A beat crossed inside this window is
   * queued (never dropped) and shown when the window closes.
   *
   * 0 = fire the moment a threshold is crossed. That's safe because
   * crossedBeat() already collapses a multi-threshold jump into just the
   * highest one, so a single big round can't stack two placards — and the
   * drain refuses to start one while another is on screen. Separate rounds
   * are naturally spaced by however long people take to order again.
   *
   * Raise it (e.g. 15_000) only if two placards in quick succession ever
   * feels pushy in practice.
   */
  cooldownMs: 0,
  /** How long a button's italic reaction line holds before the placard closes. */
  reactionHoldMs: 1700,
  /** Where the 25.6s dance is cut in the finale. */
  danceSeconds: 15,
  /**
   * Hangs from the rope on the placards (64px circle). This is the NineFinds
   * mark, NOT Chintu — the placard copy is written in the third person
   * ("Chintu is a little excited"), so the app is the one talking about him.
   * His own face on a message he isn't speaking would make him narrate himself.
   */
  ropeBadgeSrc: "/nine_logo_vector_4.svg",

  /**
   * Chintu's portrait, used only where he IS the subject: the ID card badge
   * (60px rounded square). Cropped from a render of the actual 3D model, so
   * the little face and the monkey who turns up at ₹2,000 read as the same
   * character. Any square image works — 384px covers 3x displays at this size.
   */
  faceSrc: "/images/chintu-face.webp",
  /** true: OK on the story card closes everything. false: returns to the placard. */
  storyClosesPlacard: true,
  /** Bump to invalidate saved arcs when the persisted shape changes. */
  storageVersion: "v3",
};
